import React, { Suspense, lazy } from "react";
import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ToastContainer } from "react-toastify";
import { ThemeProvider } from "@material-tailwind/react";

import "./App.css";
import MoodleCourses from "./Components/MoodleCourses/MoodleCourses";

const AuthProvider = lazy(() => import("./AuthProvider"));
const useAuthStore = require("./stores/useAuthStore").default;

const GoogleRedirectHandler = lazy(() => import("./Pages/GoogleRedirectHandler/GoogleRedirectHandler"));
const ConfirmEmail = lazy(() => import("./Pages/ConfirmEmail/ConfirmEmail"));

// Layouts
const Layout = lazy(() => import("./Components/Layout/Layout"));
const DashboardLayout = lazy(() => import("./Components/Layout/DashboardLayout"));

// Pages
const Home = lazy(() => import("./Components/Home/Home"));
const Landingpage = lazy(() => import("./Components/Landingpage/Page"));
const Courses = lazy(() => import("./Components/Courses/Courses"));
const Coursedetails = lazy(() => import("./Components/Coursedetails/Coursedetails"));
const Courseoverview = lazy(() => import("./Components/Courseoverview/CourseOverview"));
const Usercourses = lazy(() => import("./Components/Usercourses/Courses"));
const Trainerprofile = lazy(() => import("./Components/Trainerprofile/Trainerprofile"));
const Policy = lazy(() => import("./Pages/Policy/Policy"));

// Auth
const Login = lazy(() => import("./Components/Login/Login"));
const SignUp = lazy(() => import("./Components/SignUp/SignUp"));
const AddPasswordPage = lazy(() => import("./Components/AddPassword/AddPassword"));
const ResetPassword = lazy(() => import("./Components/Resetpassword/Resetpassword"));
const ForgotPassword = lazy(() => import("./Components/ForgotPassword/ForgotPassword"));
const LinkedInLogin = lazy(() => import("./Components/LinkedInLogin/LinkedInLogin"));

// Dashboard
const Dashboard = lazy(() => import("./Components/Dashboard/Dashboard"));
const Trainers = lazy(() => import("./Components/Dashboard/Trainers/Trainers"));
const Users = lazy(() => import("./Components/Dashboard/Users/Users"));
const Maincourses = lazy(() => import("./Components/Dashboard/Maincourses/Courses-dashboard"));

// Features
const Payment = lazy(() => import("./Components/Payment/Payment"));
const Moodle = lazy(() => import("./Components/MoodleCourses/MoodleCourses"));

const queryClient = new QueryClient();

const loadingScreen = (
  <div className="flex justify-center items-center h-screen my-12">
    <span className="loader my-8"></span>
  </div>
);

// Route guards
const ProtectedRoute = ({ children }) => {
  const { token } = useAuthStore.getState();
  return token ? children : <Navigate to="/login" replace />;
};

const AdminRoute = ({ children }) => {
  const { token, user } = useAuthStore.getState();
  const isAdmin = user?.role === "admin";
  return token && isAdmin ? children : <Navigate to="/not-found" replace />;
};

const GuestOnlyRoute = ({ children }) => {
  const { token } = useAuthStore.getState();
  return !token ? children : <Navigate to="/" replace />;
};

const UserOnlyRoute = ({ children }) => {
  const { token, user } = useAuthStore.getState();
  const isUser = token && user?.role !== "admin";
  return isUser ? children : <Navigate to="/" replace />;
};

const routes = createBrowserRouter([
  {
    path: "/",
    element: <Suspense fallback={loadingScreen}><Layout /></Suspense>,
    children: [
      { index: true, element: <Suspense fallback={loadingScreen}><Home /></Suspense> },
      { path: "home", element: <Suspense fallback={loadingScreen}><Home /></Suspense> },
      { path: "courses", element: <Suspense fallback={loadingScreen}><Courses /></Suspense> },
      { path: "course-details/:id", element: <Suspense fallback={loadingScreen}><Coursedetails /></Suspense> },
      { path: "trainer-profile/:id", element: <Suspense fallback={loadingScreen}><Trainerprofile /></Suspense> },
      { path: "course-overview/:id", element: <Suspense fallback={loadingScreen}><Courseoverview /></Suspense> },
      { path: "policy", element: <Suspense fallback={loadingScreen}><Policy /></Suspense> },
      { path: "moodle", element: <Suspense fallback={loadingScreen}><Moodle /></Suspense> },
      { path: "landing-page", element: <Suspense fallback={loadingScreen}><Landingpage /></Suspense> },
      { path: "payment", element: <ProtectedRoute><Suspense fallback={loadingScreen}><Payment /></Suspense></ProtectedRoute> },
      { path: "add-password", element: <Suspense fallback={loadingScreen}><AddPasswordPage /></Suspense> },
      { path: "login", element: <GuestOnlyRoute><Suspense fallback={loadingScreen}><Login /></Suspense></GuestOnlyRoute> },
      { path: "signup", element: <GuestOnlyRoute><Suspense fallback={loadingScreen}><SignUp /></Suspense></GuestOnlyRoute> },
      { path: "forgot-password", element: <GuestOnlyRoute><Suspense fallback={loadingScreen}><ForgotPassword /></Suspense></GuestOnlyRoute> },
      { path: "/auth/password-reset", element: <Suspense fallback={loadingScreen}><ResetPassword /></Suspense> },
      { path: "linkedin", element: <Suspense fallback={loadingScreen}><LinkedInLogin /></Suspense> },
      { path: "usercourses", element: <ProtectedRoute><Suspense fallback={loadingScreen}><Usercourses /></Suspense></ProtectedRoute> },
      { path: "/payment-success", element: <UserOnlyRoute><Suspense fallback={loadingScreen}><Usercourses /></Suspense></UserOnlyRoute> },
      { path: "auth/google/callback", element: <Suspense fallback={loadingScreen}><GoogleRedirectHandler /></Suspense> },
      { path: "auth/confirm", element: <Suspense fallback={loadingScreen}><ConfirmEmail /></Suspense> }
    ]
  },
  {
    path: "/dashboard",
    element: <AdminRoute><Suspense fallback={loadingScreen}><DashboardLayout /></Suspense></AdminRoute>,
    children: [
      { index: true, element: <Suspense fallback={loadingScreen}><Dashboard /></Suspense> },
      { path: "trainers", element: <Suspense fallback={loadingScreen}><Trainers /></Suspense> },
      { path: "users", element: <Suspense fallback={loadingScreen}><Users /></Suspense> },
      { path: "courses", element: <Suspense fallback={loadingScreen}><Maincourses /></Suspense> },
      { path: "moodle-courses", element: <Suspense fallback={loadingScreen}><MoodleCourses /></Suspense> }
    ]
  },
  {
    path: "/not-found",
    element: <div className="text-center p-10 text-xl">Page Not Found</div>
  }
]);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={loadingScreen}>
        <AuthProvider>
          <ThemeProvider>
            <RouterProvider router={routes} />
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
            />
          </ThemeProvider>
        </AuthProvider>
      </Suspense>
    </QueryClientProvider>
  );
}

export default App;
